// src/pages/Confidentialite.js - Politique de Confidentialité (RGPD)
import React from 'react';
import { Link } from 'react-router-dom';
import './Legal.css';

function Confidentialite() {
  return (
    <div className="legal-container">
      <div className="legal-card">
        <div className="legal-header">
          <h1>🔒 Politique de Confidentialité</h1>
          <p className="legal-date">Dernière mise à jour : Juillet 2026</p>
        </div>

        <div className="legal-content">
          <section>
            <h2>1. Introduction</h2>
            <p>
              Chez FirstArabic, nous accordons une grande importance à la protection de vos 
              données personnelles. Cette politique de confidentialité explique comment nous 
              collectons, utilisons, stockons et protégeons vos informations conformément au 
              Règlement Général sur la Protection des Données (RGPD).
            </p>
          </section>

          <section>
            <h2>2. Responsable du Traitement</h2>
            <div className="info-block">
              <p><strong>FirstArabic</strong></p>
              <p>Email : <a href="mailto:contact@firstarabic.com">contact@firstarabic.com</a></p>
              <p>Adresse : 113 boulevard de Strasbourg, 76600 Le Havre, France</p>
            </div>
          </section>

          <section>
            <h2>3. Données Collectées</h2>
            <p>Nous collectons les données suivantes :</p>
            
            <h3>3.1 Données d'identification</h3>
            <ul>
              <li>Nom et prénom</li>
              <li>Adresse email</li>
              <li>Numéro de téléphone (optionnel)</li>
              <li>Photo de profil (pour les professeurs)</li>
            </ul>

            <h3>3.2 Données de connexion</h3>
            <ul>
              <li>Adresse IP</li>
              <li>Type de navigateur</li>
              <li>Date et heure de connexion</li>
            </ul>

            <h3>3.3 Données de paiement</h3>
            <ul>
              <li>Informations de facturation</li>
              <li>Historique des transactions</li>
            </ul>
            <p className="note">
              <strong>Note :</strong> Les données bancaires (numéro de carte) sont traitées 
              directement par notre prestataire de paiement Stripe et ne sont jamais stockées 
              sur nos serveurs.
            </p>

            <h3>3.4 Données d'utilisation</h3>
            <ul>
              <li>Historique des cours</li>
              <li>Évaluations et commentaires</li>
              <li>Messages échangés sur la plateforme</li>
            </ul>
          </section>

          <section>
            <h2>4. Finalités du Traitement</h2>
            <p>Vos données sont utilisées pour :</p>
            <table className="legal-table">
              <thead>
                <tr>
                  <th>Finalité</th>
                  <th>Base légale</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Création et gestion de votre compte</td>
                  <td>Exécution du contrat</td>
                </tr>
                <tr>
                  <td>Mise en relation élèves/professeurs</td>
                  <td>Exécution du contrat</td>
                </tr>
                <tr>
                  <td>Traitement des paiements</td>
                  <td>Exécution du contrat</td>
                </tr>
                <tr>
                  <td>Envoi de notifications (cours, rappels)</td>
                  <td>Exécution du contrat</td>
                </tr>
                <tr>
                  <td>Amélioration de nos services</td>
                  <td>Intérêt légitime</td>
                </tr>
                <tr>
                  <td>Newsletter (si consentement)</td>
                  <td>Consentement</td>
                </tr>
                <tr>
                  <td>Réponse à vos demandes</td>
                  <td>Intérêt légitime</td>
                </tr>
              </tbody>
            </table>
          </section>

          <section>
            <h2>5. Durée de Conservation</h2>
            <table className="legal-table">
              <thead>
                <tr>
                  <th>Type de données</th>
                  <th>Durée de conservation</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Données de compte</td>
                  <td>Durée de l'inscription + 3 ans</td>
                </tr>
                <tr>
                  <td>Données de paiement</td>
                  <td>10 ans (obligation légale)</td>
                </tr>
                <tr>
                  <td>Historique des cours</td>
                  <td>3 ans après le dernier cours</td>
                </tr>
                <tr>
                  <td>Données de connexion</td>
                  <td>1 an</td>
                </tr>
              </tbody>
            </table>
          </section>

          <section>
            <h2>6. Destinataires des Données</h2>
            <p>Vos données peuvent être partagées avec :</p>
            <ul>
              <li><strong>Stripe</strong> : traitement des paiements</li>
              <li><strong>Supabase</strong> : hébergement de la base de données</li>
              <li><strong>Vercel</strong> : hébergement du site</li>
              <li><strong>Resend</strong> : envoi d'emails transactionnels</li>
              <li><strong>Agora</strong> : visioconférence pour les cours</li>
            </ul>
            <p>
              Ces prestataires sont soumis à des obligations de confidentialité et de sécurité.
              Certains peuvent être situés hors de l'Union Européenne, avec des garanties
              appropriées (clauses contractuelles types de la Commission européenne, ou
              certification au titre de l'EU-US Data Privacy Framework).
            </p>
          </section>

          <section>
            <h2>7. Vos Droits</h2>
            <p>Conformément au RGPD, vous disposez des droits suivants :</p>
            
            <div className="rights-grid">
              <div className="right-item">
                <h4>📋 Droit d'accès</h4>
                <p>Obtenir une copie de vos données personnelles</p>
              </div>
              <div className="right-item">
                <h4>✏️ Droit de rectification</h4>
                <p>Corriger des données inexactes ou incomplètes</p>
              </div>
              <div className="right-item">
                <h4>🗑️ Droit à l'effacement</h4>
                <p>Demander la suppression de vos données</p>
              </div>
              <div className="right-item">
                <h4>⏸️ Droit à la limitation</h4>
                <p>Limiter le traitement de vos données</p>
              </div>
              <div className="right-item">
                <h4>📦 Droit à la portabilité</h4>
                <p>Recevoir vos données dans un format structuré</p>
              </div>
              <div className="right-item">
                <h4>🚫 Droit d'opposition</h4>
                <p>Vous opposer au traitement de vos données</p>
              </div>
            </div>

            <p>
              Pour exercer ces droits, contactez-nous à : 
              <a href="mailto:contact@firstarabic.com">contact@firstarabic.com</a>
            </p>
            <p>
              Vous disposez également du droit d'introduire une réclamation auprès de la CNIL 
              (<a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer">www.cnil.fr</a>).
            </p>
          </section>

          <section>
            <h2>8. Sécurité des Données</h2>
            <p>
              Nous mettons en œuvre des mesures techniques et organisationnelles appropriées 
              pour protéger vos données :
            </p>
            <ul>
              <li>Chiffrement des données en transit (HTTPS/TLS)</li>
              <li>Authentification sécurisée</li>
              <li>Accès restreint aux données personnelles</li>
              <li>Sauvegardes régulières</li>
              <li>Surveillance des accès</li>
            </ul>
          </section>

          <section>
            <h2>9. Cookies</h2>
            <p>Notre site utilise des cookies essentiels pour :</p>
            <ul>
              <li>Maintenir votre session de connexion</li>
              <li>Mémoriser vos préférences</li>
              <li>Assurer la sécurité du site</li>
            </ul>
            <p>
              Ces cookies sont strictement nécessaires au fonctionnement du site et ne 
              requièrent pas votre consentement.
            </p>
          </section>

          <section>
            <h2>10. Modifications</h2>
            <p>
              Nous nous réservons le droit de modifier cette politique de confidentialité. 
              En cas de modification substantielle, nous vous en informerons par email ou 
              via une notification sur le site.
            </p>
          </section>

          <section>
            <h2>11. Contact</h2>
            <p>
              Pour toute question concernant cette politique ou vos données personnelles :
            </p>
            <ul>
              <li>Email : <a href="mailto:contact@firstarabic.com">contact@firstarabic.com</a></li>
              <li>Formulaire : <Link to="/contact">Page de contact</Link></li>
            </ul>
          </section>
        </div>

        <div className="legal-footer">
          <Link to="/">← Retour à l'accueil</Link>
          <div className="legal-links">
            <Link to="/cgu">CGU</Link>
            <Link to="/mentions-legales">Mentions légales</Link>
            <Link to="/contact">Contact</Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Confidentialite;
